import React, { FC } from 'react'
import styled from 'styled-components'

const StyledButton = styled.button`
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-rendering: geometricPrecision;
  text-box-trim: both;
  text-box-edge: cap alphabetic;
  -webkit-text-box-trim: both;
  -webkit-text-box-edge: cap alphabetic;
  will-change: border-radius;
  transform: translateZ(0);

  /* Synchronized transition matching content slide timing and easing */
  transition-property: border-radius, background-color, border-color, color, box-shadow;
  transition-duration: 700ms;
  transition-timing-function: cubic-bezier(0.16, 1, 0.3, 1);

  &.rounded-large {
    border-radius: min(var(--radius-large, 9999px), calc(var(--spacing-extra-huge, 56px) / 2));
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-radius: min(var(--radius-hover-large, 24px), calc(var(--spacing-extra-huge, 56px) / 2));
      }
    }
  }

  &.rounded-base {
    border-radius: min(var(--radius-base, 9999px), calc(var(--spacing-huge, 48px) / 2));
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-radius: min(var(--radius-hover-base, 16px), calc(var(--spacing-huge, 48px) / 2));
      }
    }
  }

  &.rounded-small {
    border-radius: min(var(--radius-small, 9999px), calc(var(--wcag-min-target, 44px) / 2));
    @media (hover: hover) and (pointer: fine) {
      &:hover {
        border-radius: min(var(--radius-hover-small, 12px), calc(var(--wcag-min-target, 44px) / 2));
      }
    }
  }

  .button-slide-row {
    will-change: transform;
    backface-visibility: hidden;
    -webkit-backface-visibility: hidden;
    transform: translate3d(0, var(--tw-translate-y, 0), 0);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
  }
`

export enum ButtonSizes {
  small = 'small',
  medium = 'medium',
  large = 'large',
}

export type ButtonVariant = 'primary' | 'error' | 'warning' | 'danger'

export interface IButtonProps {
  children?: React.ReactNode
  onClick?: (e?: React.MouseEvent<HTMLButtonElement>) => void
  className?: string
  size?: ButtonSizes
  isOutline?: boolean
  isDisabled?: boolean
  isCircle?: boolean
  type?: 'button' | 'submit' | 'reset'
  hoverChildren?: React.ReactNode
  noSlide?: boolean
  title?: string
  variant?: ButtonVariant
  form?: string
  showArrow?: boolean
}

export type ISemiRoundedButtonProps = IButtonProps

const RADIUS_CLASSES: Record<ButtonSizes, string> = {
  [ButtonSizes.small]: 'rounded-small hover:rounded-hover-small lg:hover:rounded-hover-small',
  [ButtonSizes.medium]: 'rounded-base hover:rounded-hover-base lg:hover:rounded-hover-base',
  [ButtonSizes.large]: 'rounded-large hover:rounded-hover-large lg:hover:rounded-hover-large',
}

const SIZE_CLASSES: Record<ButtonSizes, { withArrow: string; default: string }> = {
  [ButtonSizes.small]: {
    withArrow: 'h-wcag-min-target min-h-wcag-min-target pl-medium pr-small text-body-medium font-bold',
    default: 'h-wcag-min-target min-h-wcag-min-target px-medium text-body-medium font-bold',
  },
  [ButtonSizes.medium]: {
    withArrow: 'h-huge min-h-huge pl-semi-large pr-base text-body-large font-bold',
    default: 'h-huge min-h-huge px-semi-large text-body-large font-bold',
  },
  [ButtonSizes.large]: {
    withArrow: 'h-extra-huge min-h-extra-huge pl-large pr-semi-large text-body-large font-bold',
    default: 'h-extra-huge min-h-extra-huge px-large text-body-large font-bold',
  },
}

const OUTLINE_STYLES =
  'bg-transparent border-1 border-outline-low text-on-surface lg:hover:bg-transparent'

const FILLED_STYLES = 'bg-primary text-on-primary-unstable lg:hover:bg-primary-hover'

const DISABLED_OUTLINE_STYLES =
  'bg-transparent border-2 border-outline-low text-on-surface-disabled cursor-not-allowed pointer-events-none'

const DISABLED_FILLED_STYLES =
  'bg-surface-disabled text-on-surface-disabled cursor-not-allowed pointer-events-none'

const ArrowUpRightIcon: FC<{ className?: string }> = ({ className = '' }) => (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className={`block ${className}`}
    aria-hidden="true"
  >
    <line x1="4" y1="12" x2="12" y2="4" />
    <polyline points="6 4 12 4 12 10" />
  </svg>
)

export interface IButtonContext {
  showArrow?: boolean
}

export const ButtonContext = React.createContext<IButtonContext>({
  showArrow: true,
})

export const ButtonProvider: FC<{ showArrow?: boolean; children: React.ReactNode }> = ({
  showArrow = true,
  children,
}) => (
  <ButtonContext.Provider value={{ showArrow }}>{children}</ButtonContext.Provider>
)

export const Button: FC<IButtonProps> = ({
  onClick,
  className = '',
  children,
  size = ButtonSizes.large,
  isOutline = false,
  isDisabled = false,
  isCircle = false,
  type = 'button',
  hoverChildren,
  noSlide = false,
  title,
  form,
  showArrow,
  variant = 'primary',
}) => {
  const context = React.useContext(ButtonContext)
  const effectiveShowArrow = showArrow !== undefined ? showArrow : (context?.showArrow ?? true)
  const shouldShowArrow = Boolean(effectiveShowArrow && !isCircle && children)

  const sizeStyles = isCircle
    ? 'w-extra-huge h-extra-huge min-w-extra-huge min-h-extra-huge shrink-0 p-0'
    : SIZE_CLASSES[size][shouldShowArrow ? 'withArrow' : 'default']

  const radiusStyles = isCircle ? 'rounded-full' : RADIUS_CLASSES[size]

  let typeStyles: string
  if (isDisabled) {
    typeStyles = isOutline ? DISABLED_OUTLINE_STYLES : DISABLED_FILLED_STYLES
  } else if (variant === 'danger' || variant === 'error') {
    typeStyles = isOutline
      ? 'bg-transparent border-1 border-red text-red lg:hover:bg-red/10'
      : 'bg-red text-white lg:hover:bg-darkRed'
  } else if (variant === 'warning') {
    typeStyles = isOutline
      ? 'bg-transparent border-1 border-yellow text-yellow lg:hover:bg-yellow/10'
      : 'bg-yellow text-black lg:hover:bg-darkYellow'
  } else {
    typeStyles = isOutline ? OUTLINE_STYLES : FILLED_STYLES
  }

  const buttonStyles = `group relative overflow-hidden inline-flex items-center justify-center font-palanquin antialiased select-none transition-all duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${radiusStyles} ${sizeStyles} ${typeStyles} ${className}`.trim()

  const arrowContainerSize = size === ButtonSizes.small ? 'w-3 h-3' : 'w-3.5 h-3.5'

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isDisabled) return
    onClick?.(e)
  }

  const renderContent = (content: React.ReactNode) => (
    <span className="inline-flex items-center justify-center">
      <span className="inline-flex items-center [text-box-trim:both] [text-box-edge:cap_alphabetic]">{content}</span>
      {shouldShowArrow && (
        <span
          className={`ml-extra-small shrink-0 inline-flex items-center justify-center leading-none pointer-events-none ${arrowContainerSize}`}
          aria-hidden="true"
        >
          <ArrowUpRightIcon className="w-full h-full" />
        </span>
      )}
    </span>
  )

  return (
    <StyledButton
      type={type}
      form={form}
      title={title}
      className={buttonStyles}
      onClick={handleClick}
      disabled={isDisabled}
    >
      {noSlide ? (
        renderContent(children)
      ) : (
        <span className="relative inline-grid items-center justify-center h-full">
          {/* Visible normal row */}
          <span className="button-slide-row row-start-1 col-start-1 h-full inline-flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] lg:group-hover:-translate-y-full whitespace-nowrap">
            {renderContent(children)}
          </span>
          {/* Visible hover row */}
          <span className="button-slide-row row-start-1 col-start-1 h-full inline-flex items-center justify-center transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] translate-y-full lg:group-hover:translate-y-0 whitespace-nowrap">
            {renderContent(hoverChildren || children)}
          </span>
          {/* Invisible sizing row for width calculation */}
          <span className="row-start-1 col-start-1 h-full invisible opacity-0 pointer-events-none whitespace-nowrap inline-flex items-center justify-center" aria-hidden="true">
            {renderContent(children)}
          </span>
          <span className="row-start-1 col-start-1 h-full invisible opacity-0 pointer-events-none whitespace-nowrap inline-flex items-center justify-center" aria-hidden="true">
            {renderContent(hoverChildren || children)}
          </span>
        </span>
      )}
    </StyledButton>
  )
}

export const SemiRoundedButton = Button
export default Button
